import React, { useState, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormGroup,
  TextField,
  FormControl,
} from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as auth from '../_redux/authRedux';
import { setSnackbar } from '../../../../redux/snackbar';
import { toAbsoluteUrl } from '../../../../Utils/_helpers';

const ChangePassword = (props) => {
  const { intl, isReset = true } = props;
  const {
    is_password_expired,
    is_password_expiring,
    password_reset_required,
    pwd_exp_remaining_days,
  } = useSelector((state) => state.auth);

  const [showModal, setShowModal] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    let val = is_password_expiring || (password_reset_required !== 2) || is_password_expired;
    setShowModal(val);
  },[is_password_expired, password_reset_required, is_password_expired]);

  const passwordValidationSchema = Yup.object().shape({
    oldPassword: isReset
      ? Yup.string()
          .required(
            intl.formatMessage({
              id: 'Auth_validation-required-field',
            })
          )
      : Yup.string(),
    newPassword: isReset
      ? Yup.string()
          .notOneOf(
            [Yup.ref('oldPassword')],
            'New password cannot be equal to current password'
          )
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!#^()%*?&])[A-Za-z\d$@$!#^()%*?&]{8,12}$/,
            'Enter atleast one uppercase letter, one lowercase letter, one digit, and one special character. Password should be 8-12 digits long.'
          )
          .required(
            intl.formatMessage({ id: 'Auth_validation-required-field' })
          )
      : Yup.string()
          .min(8, 'Password should be 8-12 digits long')
          .max(12,'Password should be 8-12 digits long')
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!#^()%*?&])[A-Za-z\d$@$!#^()%*?&]{8,12}$/,
            'Enter atleast one uppercase letter, one lowercase letter, one digit, and one special character. Password should be 8-12 digits long.'
          )
          .required(
            intl.formatMessage({
              id: 'Auth_validation-required-field',
            })
          ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required(
        intl.formatMessage({
          id: 'Auth_validation-required-field',
        })
      ),
  });

  const successHandler = (resp) => {
    formik.setStatus({ isSuccess: true, message: resp });
    dispatch(setSnackbar(true, 'success', resp));
    formik.setSubmitting(false);
    setShowModal(false);
    formik.resetForm();
  };

  const errorHandler = (resp) => {
    formik.setStatus({ isSuccess: false, message: resp });
    formik.setSubmitting(false);
    dispatch(setSnackbar(true, 'error', resp));
    // setShowModal(false);
  };

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    // resetForm();
    setSubmitting(true);
    const payload = {
      old_password: values.oldPassword,
      new_password: values.newPassword,
      confirm_password: values.confirmPassword,
    };
    if (!isReset) {
      delete payload.old_password;
    }

    let data = '';
    let len = Object.keys(payload).length;
    Object.keys(payload).forEach((x, i) => {
      data += `${x}=${encodeURIComponent(payload[x])}`;
      if (i < len - 1) {
        data += '&';
      }
    });

    dispatch(auth.actions.changePassword(data, successHandler, errorHandler));
  };

  const handleClose = () => {
    formik.resetForm();
    setShowModal(false);
  };

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: passwordValidationSchema,
    onSubmit: handleSubmit,
  });

  let assumeId = localStorage.getItem('assume_id');
  assumeId = (assumeId === 'null' || assumeId === '' || assumeId === 'None') ? false : assumeId;

  if (assumeId) {
    return <></>;
  }

  return (
    <>
      <Dialog
        disableEscapeKeyDown
        disableBackdropClick
        open={showModal}
        fullWidth
      >
        <DialogTitle
          style={{
            background: 'linear-gradient(264deg, #722192 20%, #1e4a94 80%)',
            color: '#fff',
          }}
        >
          {
            <p style={{ margin: 0 }}>
              {isReset ? 'Change Password' : 'Reset Password'}
              <span>
                {
                  !is_password_expired && 
                  <img
                  style={{ float: 'right', cursor: 'pointer' }}
                  alt="close"
                  id="close"
                  title="close"
                  src={toAbsoluteUrl('/media/sr/icons/close-red.svg')}
                  className="close-modal"
                  onClick={handleClose}
                  />
                }
              </span>
            </p>
          }
        </DialogTitle>
        <DialogContent style={{ padding: '2rem' }}>
          {pwd_exp_remaining_days > 0 && (
            <>
              <span
                style={{
                  display: 'flex',
                  justifyContent: 'start',
                  alignItems: 'center',
                }}
              >
                <img
                  style={{ float: 'left' }}
                  alt="info"
                  id="info"
                  title="info"
                  src={toAbsoluteUrl('/media/sr/icons/info.svg')}
                />
                <p style={{ margin: 0 }}>{`Your password will expire in ${
                  pwd_exp_remaining_days === 1
                    ? `${pwd_exp_remaining_days} day.`
                    : `${pwd_exp_remaining_days} days.`
                }`}</p>
              </span>
            </>
          )}
          <Box
            component="form"
            onSubmit={formik.handleSubmit}
            style={{ padding: '1.5rem' }}
          >
            <FormGroup style={{ padding: '1rem 0' }}>
              <FormControl>
                <TextField
                  type="password"
                  size="small"
                  id="oldPassword"
                  name="oldPassword"
                  label="Enter old password"
                  variant="outlined"
                  value={formik.values.oldPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.oldPassword &&
                    Boolean(formik.errors.oldPassword)
                  }
                  helperText={formik.errors.oldPassword}
                />
              </FormControl>
            </FormGroup>
            <FormGroup style={{ padding: '1rem 0' }}>
              <FormControl>
                <TextField
                  type="password"
                  size="small"
                  id="newPassword"
                  name="newPassword"
                  label="Enter new password"
                  variant="outlined"
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.newPassword &&
                    Boolean(formik.errors.newPassword)
                  }
                  helperText={formik.errors.newPassword}
                />
              </FormControl>
            </FormGroup>
            <FormGroup style={{ padding: '1rem 0' }}>
              <FormControl>
                <TextField
                  type="password"
                  size="small"
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Enter confirm password"
                  variant="outlined"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.confirmPassword &&
                    Boolean(formik.errors.confirmPassword)
                  }
                  helperText={formik.errors.confirmPassword}
                />
              </FormControl>
            </FormGroup>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              style={{ float: 'right', color: '#fff', margin: '1rem 0' }}
            >
              Submit
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default injectIntl(connect(null, auth.actions)(ChangePassword));
